/**
 * E-ticaret satis hunisi — legacy `frontend/public/js/kpi.js` `renderEcomFunnel`
 * portu, saf geometri/oran hesabi (docs/faz5-arayuz-spec.md §5: "huni SVG
 * bileşeni — eski renderEcomFunnel port"). Kaynak: `ozet.trafik.huni`
 * (`{label, count}[]`; backend NOTU: `app/core/analytics/gidisat.py`
 * `trafik_derle` bu diziyi su an her zaman BOS doner — GA4 v1alpha
 * tenant-parametreli degil — bu yuzden EmptyState yolu canli yoldur).
 */

export interface FunnelStage {
  label: string;
  count: number;
  prev?: number | null;
  sub?: string;
}

export const FUNNEL_WIDTH = 320;
export const FUNNEL_STAGE_HEIGHT = 60;
export const FUNNEL_GAP = 6;
/** Son asamanin alt genisligi = kendi ust genisliginin bu orani (legacy sabiti). */
const LAST_STAGE_TAPER = 0.8;
/** En dar asama genisligi (legacy: `W - 96`'nin sabiti). */
const MIN_STAGE_WIDTH = 96;

export interface FunnelStageGeometry {
  index: number;
  label: string;
  count: number;
  topWidth: number;
  bottomWidth: number;
  y: number;
  /** SVG path `d` — yamuk (trapez). */
  path: string;
}

/** i'inci asamanin ust genisligi — dogrusal daralan yamuk zinciri. */
export function stageTopWidth(index: number, stageCount: number, width = FUNNEL_WIDTH): number {
  if (stageCount <= 1) return width;
  return width - index * ((width - MIN_STAGE_WIDTH) / (stageCount - 1));
}

/** "elimizde veri varsa" kapisi: en az 2 asama ve ilk asama > 0. */
export function hasFunnelData(stages: FunnelStage[] | null | undefined): boolean {
  return Array.isArray(stages) && stages.length >= 2 && Number(stages[0]?.count) > 0;
}

/** Asama → onceki asamaya gore donusum orani (0-1); ilk asama icin null (o 100% sabit gosterilir). */
export function stageConversionRatio(stages: FunnelStage[], index: number): number | null {
  if (index <= 0 || index >= stages.length) return null;
  const prevCount = Number(stages[index - 1]?.count);
  if (!(prevCount > 0)) return null;
  return Number(stages[index].count) / prevCount;
}

/** Ilk → son asama toplam donusum orani (0-1). */
export function totalConversionRatio(stages: FunnelStage[]): number | null {
  if (stages.length === 0) return null;
  const first = Number(stages[0]?.count);
  const last = Number(stages[stages.length - 1]?.count);
  if (!(first > 0)) return null;
  return last / first;
}

/** Onceki donem icin ayni toplam oran (`prev` alanlari varsa). */
export function totalConversionRatioPrev(stages: FunnelStage[]): number | null {
  if (stages.length === 0) return null;
  const first = stages[0]?.prev;
  const last = stages[stages.length - 1]?.prev;
  if (first == null || last == null || !(Number(first) > 0)) return null;
  return Number(last) / Number(first);
}

/** Her asama icin yamuk geometrisi (SVG path dahil) — legacy `genislik`/`d` hesabi. */
export function funnelGeometry(stages: FunnelStage[], width = FUNNEL_WIDTH, height = FUNNEL_STAGE_HEIGHT, gap = FUNNEL_GAP): FunnelStageGeometry[] {
  const n = stages.length;
  return stages.map((stage, i) => {
    const top = stageTopWidth(i, n, width);
    const bottom = i < n - 1 ? stageTopWidth(i + 1, n, width) : top * LAST_STAGE_TAPER;
    const y = i * (height + gap);
    const path =
      `M${(width - top) / 2},${y} ` +
      `L${(width + top) / 2},${y} ` +
      `L${(width + bottom) / 2},${y + height} ` +
      `L${(width - bottom) / 2},${y + height} Z`;
    return { index: i, label: stage.label, count: Number(stage.count) || 0, topWidth: top, bottomWidth: bottom, y, path };
  });
}

/** SVG viewBox yuksekligi (n asama icin). */
export function funnelViewBoxHeight(stageCount: number, height = FUNNEL_STAGE_HEIGHT, gap = FUNNEL_GAP): number {
  return stageCount * (height + gap) - gap;
}
